// EventsList.tsx
import React from 'react';
import { Place } from './MapBetweenPlaces.tsx';

interface EventsListProps {
  events: Place[];
  markers: any[];
  selectedEventRef: React.MutableRefObject<Place | null>;
  selectedEvent: Place | null;
  setSelectedEvent: React.Dispatch<React.SetStateAction<Place | null>>;
  eventRefs: React.MutableRefObject<{ [key: string]: HTMLLIElement | null }>;
  location1LL: { lat: number; lng: number } | null;
  calculateDistance: (loc1: { lat: number; lng: number }, loc2: { lat: number; lng: number }) => number;
  expandedDescriptions: { [key: string]: boolean };
  toggleDescription: (placeId: string) => void;
  focusMapOnMarker: (marker: any, place?: any) => void;
  displayedTitle: string;
}

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return dateStr;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${mins}`;
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}${timeStr !== '00:00' ? `, ${timeStr}` : ''}`;
};

const getShortDisplayAddress = (title?: string, address?: string): string => {
  if (!address) return ''; // or return something like title ?? ''

  // Check if the address starts with the title followed by a comma
  if (title && address.startsWith(`${title},`)) {
    address = address.slice(title.length + 1).trim();
  }

  const parts = address.split(',');
  if (parts.length > 2) {
    // Remove the last part (country) or two parts if needed
    return parts.slice(0, -1).join(',').trim();
  }

  return address.trim();
};


const EventsList: React.FC<EventsListProps> = ({
  events,
  markers,
  selectedEventRef,
  selectedEvent,
  setSelectedEvent,
  eventRefs,
  location1LL,
  calculateDistance,
  expandedDescriptions,
  toggleDescription,
  focusMapOnMarker,
  displayedTitle,
}) => {

    const getShortDescription = (description: string, maxLength = 10) => {
        const words = description.split(' ');
        if (words.length > maxLength) {
            return words.slice(0, maxLength).join(' ') + '...';
        }
        return description;
    };
    
  return (
    <div className="events-overlay">
      <div className="events-overlay-content">
        {displayedTitle && <h4 className="list-title">{displayedTitle}</h4>}
        <p className="events-col-meta">Along your route · upcoming</p>
        {events.length > 0 ? (
          <ul className="events-list">
 {events.map((event) => (
  <li
    key={event.place_id}
    ref={(el) => {
      if (el) {
        eventRefs.current[event.place_id] = el;
      }
    }}
    className={`event-item ${selectedEvent && selectedEvent.place_id === event.place_id ? 'selected' : ''}`}
	onClick={() => {
	  setSelectedEvent(event);
	  const TOLERANCE = 0.00001;
	  let markerFound = null;
	  for (let i = 0; i < markers.length; i++) {
		const marker = markers[i];
		console.log(`Checking event marker at index ${i}:`, marker);
		if (Math.abs(marker.originalLat - event.position.lat) < TOLERANCE && Math.abs(marker.originalLng - event.position.lng) < TOLERANCE) {
		  markerFound = marker;
		  break;
		}
	  }
	  if (markerFound) {
		console.log('Marker found:', markerFound);
		focusMapOnMarker(markerFound, event);
	  } else {
		console.log('No matching marker found for event:', event);
	  }
	}}
  >
    <div className="event-image-container">
      {event.eventPhotoUrl && (
        <img
          src={event.eventPhotoUrl}
          alt={event.title}
          className="place-image"
        />
      )}
    </div>
    <div className="event-details-container">
      <h5>{event.title}</h5>
	<p className="event-meta">{event.eventType?.trim()}{event.subGenre ? ` (${event.subGenre.trim()})` : ''}{event.priceRange && event.priceRange !== 'Click to Check' ? `, Price: ` : ''}{event.priceRange && event.priceRange !== 'Click to Check' ? <b>£{event.priceRange}</b> : null}</p>
      {event.eventDates && event.eventDates.length > 0 && (
        <p className="event-date-line">
          {formatEventDate(event.eventDates[0])}
          {event.eventDates.length > 1 && <span className="event-more-dates"> +{event.eventDates.length - 1} more</span>}
        </p>
      )}
      {event.description && (
		<div className="description-container">
			<p
				className={`description-text ${expandedDescriptions[event.place_id] ? "expanded" : ""}`}
			>
				{expandedDescriptions[event.place_id]
					? event.description
					: getShortDescription(event.description)}
			</p>
			<span
				className="toggle-description"
				onClick={(e) => {
					e.stopPropagation();
					toggleDescription(event.place_id);
				}}
			>
				{expandedDescriptions[event.place_id] ? "Close" : "See Info"}
			</span>
		</div>
      )}
      <div className="find-out-more-container">
        <p>
          {getShortDisplayAddress(event.title, event.address)}
          {location1LL && event.position
            ? ` (${calculateDistance(location1LL, event.position).toFixed(2)} miles)`
            : ""}
        </p>
        <a
          href={event.ticketUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Buy Tickets
        </a>
      </div>
    </div>
  </li>
))}

          </ul>
        ) : (
          <p>No events found.</p>
        )}
      </div>
    </div>
  );
};

export default EventsList;